<template>
  <div
    ref="panelRef"
    class="irregularities-map-panel"
  >
    <map-base
      ref="mapBaseRef"
      :show-geo-search-control="false"
      class="irregularities-map-panel__map"
    />

    <!-- Opened by clicking a photograph in a report's popup. Preview and download only:
         the pictures come off the driver's device and are not ours to remove. -->
    <images-preview-dialog
      v-if="imagesPreview.isOpen"
      :show-dialog="imagesPreview.isOpen"
      :images="imagesPreview.images"
      :initial-image-index="imagesPreview.initialIndex"
      :dialog-title="imagesPreview.title"
      :enable-delete="false"
      @close="closeImagesPreview"
    />
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import polyUtil from 'polyline-encoded'
import MapBase from '@/global/components/map/MapBase.vue'
import ImagesPreviewDialog from '@/global/components/dialogs/ImagesPreviewDialog.vue'
import { useImagesPreview } from '@/modules/road-maintenance-module/winter-service-v2/common/composables/useImagesPreview'
import { isArray } from '@/global/utils/utils'
import { useIrregularityMarkers } from '@/modules/road-maintenance-module/winter-service-v2/irregularities/composables/useIrregularityMarkers'
import { IRREGULARITIES_TRANSLATION_NAMESPACE as NS } from '@/modules/road-maintenance-module/winter-service-v2/irregularities/config/constants'

// The route the vehicle drove, drawn behind the reports so the two never compete
// for attention.
const ROUTE_COLOR = '#A3E635'
const ROUTE_WEIGHT = 4

const props = defineProps({
  // DataTable hands the side-table slot its `config` - the table composable puts
  // the currently listed rows on it, so the map plots exactly what the table
  // shows without fetching anything itself.
  config: {
    type: Object,
    default: () => ({})
  }
})

const {
  state: imagesPreview,
  open: openImagesPreview,
  close: closeImagesPreview
} = useImagesPreview()

// The signs themselves, and what they say when one is opened, are the same here as on a
// work order's map - see `useIrregularityMarkers`. Only the popup's row labels differ,
// which is what the namespace decides.
const { renderMarkers } = useIrregularityMarkers({
  translationNamespace: NS,
  onPhotoPreview: openImagesPreview
})

const mapBaseRef = ref(null)
const panelRef = ref(null)
let isMapReady = false
let resizeObserver = null

// Only the reports the table has marked. A whole page of them at once would bury
// the map under overlapping routes and pins, so the table decides what is worth
// looking at and this draws exactly that.
const irregularities = computed(() => isArray(props.config?.irregularities)
  ? props.config.irregularities
  : []
)

/**
 * The route the vehicle drove, where the report carries one. Most do not, which is
 * exactly why the signs cannot be drawn as part of this.
 */
function renderRoutes () {
  mapBaseRef.value.removeAllPolylines()

  const polylines = irregularities.value.reduce((collected, irregularity) => {
    if (irregularity?.polyline) {
      collected.push({
        coordinates: polyUtil.decode(irregularity.polyline),
        options: {
          id: `irregularity-route-${irregularity.id}`,
          color: ROUTE_COLOR,
          weight: ROUTE_WEIGHT
        }
      })
    }

    return collected
  }, [])

  if (polylines.length) {
    mapBaseRef.value.generatePolylines({ polylines })
  }

  return polylines.length > 0
}

function render () {
  if (!isMapReady || !mapBaseRef.value) {
    return
  }

  const hasRoutes = renderRoutes()

  // A route already decides the view where there is one; a map of reports alone has
  // nothing else to fit to.
  renderMarkers(mapBaseRef.value, irregularities.value, { fitMarkers: !hasRoutes })
}

onMounted(() => {
  // Registering through `onMapReady` rather than the `ready` event skips MapBase's
  // own default company/Zagreb re-centering, which would otherwise run right after
  // and override the fitBounds the drawing does.
  mapBaseRef.value?.onMapReady(() => {
    isMapReady = true
    render()
  })

  // On the details screen the panel is laid out against the form beside it, so its
  // height moves with that. Leaflet only learns about a resized container when it
  // is told, and until it is it keeps painting tiles for the size it was first
  // laid out at.
  if (typeof ResizeObserver === 'undefined' || !panelRef.value) {
    return
  }

  resizeObserver = new ResizeObserver(() => mapBaseRef.value?.invalidateSize?.())
  resizeObserver.observe(panelRef.value)
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
  resizeObserver = null
})

watch(irregularities, render, { deep: true })
</script>

<style scoped>
.irregularities-map-panel {
  display: flex;
  height: 100%;
  width: 100%;
  min-height: 320px;
  border-radius: 16px;
  overflow: hidden;
  /* Leaflet's own controls/panes reach z-index 1000-9999 internally. Without its
     own stacking context that competes with page-level chrome like the sticky app
     header once the page scrolls. */
  isolation: isolate;
}

.irregularities-map-panel__map {
  flex: 1 1 auto;
}
</style>
